import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, filter, of, switchMap, take } from 'rxjs';
import { OktaAuthService } from '../services/http/okta-auth.service';
import { LoggingService } from 'src/app/shared/services/logging.service';

@Injectable({
    providedIn: 'root'
})
export class HeaderInterceptor implements HttpInterceptor {
    private tokenRefreshInProgress: boolean;
    private refreshedAccessTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(
        private oktaAuthService: OktaAuthService,
        private loggingService: LoggingService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.loggingService.logMessageInformation(this.messageBuilder(req.method, req.url), {
            endpoint: req.url,
            headers: req.headers
                .keys()
                .map(key => `${key}: ${req.headers.get(key)}`)
                .join(', '),
            body: JSON.stringify(req.body),
            method: req.method,
            params: req.params.toString()
        });

        // Determine which token to use based on URL
        const isCentralAuth = req.url.startsWith(environment.beaconApiUrl);
        const isEmber = req.url.startsWith(environment.emberApiUrl);

        if (isCentralAuth) {
            return this.useAncillaryApiAccessToken(req, next, 'CDP_CENTRAL_AUTH_TOKEN');
        } else if (isEmber) {
            return this.useAncillaryApiAccessToken(req, next, 'CDP_EMBER_TOKEN');
        } else {
            return this.useOktaAccessToken(req, next);
        }
    }

    private useOktaAccessToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.getAccessToken().pipe(
            take(1),
            switchMap(token => {
                if (req.body instanceof FormData) {
                    // For FormData bodies, allow browser to set Content-Type header
                    // multipart/form-data with boundary
                    // e.g. multipart/form-data; boundary=----WebKitFormBoundaryAbcdef123456
                    return next.handle(
                        req.clone({
                            setHeaders: {
                                Authorization: `Bearer ${token}`
                            }
                        })
                    );
                }
                return next.handle(
                    req.clone({
                        setHeaders: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        }
                    })
                );
            })
        );
    }

    private useAncillaryApiAccessToken(
        req: HttpRequest<any>,
        next: HttpHandler,
        tokenName: string
    ): Observable<HttpEvent<any>> {
        const token = localStorage.getItem(tokenName);
        if (!token) {
            throw new Error(`${tokenName} token missing for request to: ${req.url}`);
        }
        let headers: HttpHeaders;
        if (req.body instanceof FormData) {
            headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
        } else {
            headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            });
        }
        return next.handle(req.clone({ headers }));
    }

    messageBuilder(method: string, url: string) {
        return `${method} ${url}`;
    }

    getAccessToken(): Observable<string> {
        this.oktaAuthService.assertAuthenticatedState();

        if (this.oktaAuthService.isAccessTokenExpired()) {
            if (!this.tokenRefreshInProgress) {
                // First intercept initiates refresh

                this.tokenRefreshInProgress = true;
                this.refreshedAccessTokenSubject.next(null);

                return this.oktaAuthService.renewTokens().pipe(
                    take(1),
                    switchMap(tokens => {
                        const accessToken = <string>tokens.accessToken?.accessToken;
                        this.refreshedAccessTokenSubject.next(accessToken);
                        this.tokenRefreshInProgress = false;
                        return of(accessToken);
                    })
                );
            } else {
                // Consecutive requests wait for new token to arrive.
                return this.refreshedAccessTokenSubject.pipe(
                    filter(token => token != null),
                    take(1),
                    switchMap(token => of(<string>token))
                );
            }
        }

        return of(this.oktaAuthService.getAccessToken());
    }
}
